/**
 * 발이 딛는 높이.
 *
 * ## 왜 필요했나
 *
 * 램프(`RampDef`)는 계단 **디딤판을 그리는 데만** 쓰이고 있었다. 플레이어의
 * `pos.y` 를 바꾸는 코드가 어디에도 없어서, 초치성이 3층인데 **지하로 내려갈
 * 수가 없었다.** 그리고 한옥 기단을 세우면 마당에서 방으로 못 올라갔다.
 *
 * 두 문제가 사실 하나다 — 「지금 서 있는 자리의 바닥이 몇인가」를 아무도
 * 모르고 있었다.
 *
 * ## 턱은 넘고 벽은 못 넘는다
 *
 * 출입구마다 램프를 깔아 문지방을 넘게 하는 방법도 있었지만, 그건 **데이터로
 * 지형을 흉내 내는 것**이라 새 집을 지을 때마다 되풀이된다. 대신 사람이 실제로
 * 하는 것을 그대로 옮겼다: 낮은 턱은 그냥 올라서고, 허리보다 높으면 못 올라간다.
 *
 * `STEP_UP` 이 그 경계다. 이 값 하나로 기단·문지방·댓돌이 전부 해결되고,
 * 램프는 제 일(층과 층 사이)만 하면 된다.
 */

import type { FloorDef, LevelDef, RampDef } from '../../shared/level-types';

/**
 * 그냥 올라설 수 있는 높이.
 *
 * 한옥 기단이 30~45cm, 툇마루가 40cm 안팎이다. 0.55 면 그것들을 다 넘으면서
 * 허리 높이 가구(0.7~) 위로는 못 올라간다 — 책상 위를 걸어 다니면 곤란하다.
 */
export const STEP_UP = 0.55;

/**
 * 발밑이 이만큼보다 더 낮으면 **거기 있는 계단은 내 계단이 아니다.**
 *
 * 초치성의 지상 바닥판은 계단 구멍을 안 뚫어 놓아서, 지하 계단의 아래쪽 끝이
 * 지상 바닥 **밑을** 지난다. 그 위를 무심히 걸어가다 계단 구역에 들어서는
 * 순간 2.6m 아래로 떨어졌다 — 바닥은 멀쩡히 이어져 있는데.
 *
 * 계단은 발밑에 이어져 있을 때만 타는 것이 맞다. 층을 뚫고 지나가는 계단
 * 위를 지나갈 때는 그냥 바닥을 딛는다.
 */
export const STEP_DOWN = 0.7;

interface Rect {
  x1: number;
  z1: number;
  x2: number;
  z2: number;
  /** 이 판의 윗면 높이 */
  y: number;
}

export interface Ground {
  /**
   * `(x, z)` 에서 **`fromY` 에 선 사람이 딛을 수 있는** 가장 높은 바닥.
   *
   * 못 딛는 자리(허공)면 `null`. 부르는 쪽은 그때 이동을 되돌린다.
   */
  at(x: number, z: number, fromY: number): number | null;
  /**
   * 제약 없이 **그 자리에서 가장 높은 지면**. 걸어서 가는 게 아닐 때 쓴다.
   *
   * `at` 은 「지금 발이 여기 있을 때 딛을 수 있는가」를 답하느라 턱 높이와
   * 램프 소속을 따진다. 순간이동은 그 판정이 필요 없고, 오히려 방해가 된다 —
   * 언덕 위(3.6m)로 옮기는데 `at` 은 못 오른다며 거절해서 흙 속에 파묻혔다.
   */
  topAt(x: number, z: number): number | null;
  /** 램프를 뺀 순수 바닥. 편집 화면처럼 판정이 필요 없는 곳에서 쓴다 */
  rects: Rect[];
}

const inside = (r: Rect, x: number, z: number) =>
  x >= Math.min(r.x1, r.x2) && x <= Math.max(r.x1, r.x2) && z >= Math.min(r.z1, r.z2) && z <= Math.max(r.z1, r.z2);

/** 램프 위라면 경사를 따라간 높이. 아니면 null */
function rampY(r: RampDef, x: number, z: number): number | null {
  const x1 = Math.min(r.x1, r.x2);
  const x2 = Math.max(r.x1, r.x2);
  if (x < x1 || x > x2) return null;
  const zLo = Math.min(r.lowZ, r.highZ);
  const zHi = Math.max(r.lowZ, r.highZ);
  if (z < zLo || z > zHi) return null;
  // lowZ 쪽이 아래다. 그 사이를 선형으로 잇는다
  const t = (z - r.lowZ) / (r.highZ - r.lowZ);
  return r.yLow + (r.yHigh - r.yLow) * Math.max(0, Math.min(1, t));
}

export function buildGround(level: LevelDef, terrain?: { sample(x: number, z: number): number } | null): Ground {
  const rects: Rect[] = [];
  const addSlabs = (f: FloorDef) => {
    for (const [x1, z1, x2, z2, dy] of f.slabs) rects.push({ x1, z1, x2, z2, y: f.y + (dy ?? 0) });
    for (const [x1, z1, x2, z2, dy] of f.outdoorSlabs ?? []) rects.push({ x1, z1, x2, z2, y: f.y + (dy ?? 0) });
    // 툇마루도 딛는 자리다. 마루 높이는 기단과 같게 잡혀 있다
    for (const [x1, z1, x2, z2] of f.verandas ?? []) rects.push({ x1, z1, x2, z2, y: f.y + 0.4 });
  };
  for (const f of level.floors) addSlabs(f);

  if (terrain) {
    // **높이맵이 진실이다.** 판·경사로는 시작값으로 이미 격자에 들어 있다. 턱 규칙만 그대로: STEP_UP 보다 높으면 못 오른다
    return {
      rects,
      topAt: (x, z) => terrain.sample(x, z),
      at: (x, z, fromY) => { const y = terrain.sample(x, z); return y - fromY > STEP_UP ? null : y; },
    };
  }
  return {
    rects,
    topAt(x, z) {
      let best: number | null = null;
      for (const r of level.ramps) {
        const y = rampY(r, x, z);
        if (y !== null && (best === null || y > best)) best = y;
      }
      for (const r of rects) if (inside(r, x, z) && (best === null || r.y > best)) best = r.y;
      return best;
    },
    at(x, z, fromY) {
      let best: number | null = null;
      const take = (y: number) => {
        // 발보다 높으면 **턱을 넘을 수 있을 때만** 후보다.
        // 아래쪽은 얼마든지 내려갈 수 있다(떨어진다)
        if (y - fromY > STEP_UP) return;
        if (best === null || y > best) best = y;
      };
      /**
       * **계단 위에서는 계단이 진실이다.**
       *
       * 처음에는 바닥판과 램프를 같이 놓고 「높은 쪽」을 골랐다. 그런데 초치성의
       * 지상 바닥판은 계단 구멍을 안 뚫어 놔서 계단 자리까지 덮고 있다. 그래서
       * 지하 계단 위에 서면 지상 바닥(0.4)이 이겨서 **영영 내려갈 수가 없었다.**
       *
       * 램프 구역 안이면 그 경사만 본다. 계단은 원래 바닥을 뚫고 지나가는
       * 물건이라, 겹친다는 이유로 바닥이 이기면 계단이 계단이 아니게 된다.
       */
      for (const r of level.ramps) {
        const y = rampY(r, x, z);
        if (y === null) continue;
        if (y - fromY > STEP_UP) return null; // 너무 높다 — 못 오른다
        if (fromY - y > STEP_DOWN) continue; // 발밑이 아니다 — 이 계단은 지나친다
        return y;
      }
      for (const r of rects) if (inside(r, x, z)) take(r.y);
      return best;
    },
  };
}
